import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./leftPanel.module.scss";
import { PIVONY_LOGO_ON_DARK_BG } from "./logos";
import PivonyOrbitIcon from "./PivonyOrbitIcon";

function useAnimatedCounter(target, duration, active, decimals = 0) {
  const [display, setDisplay] = useState(decimals > 0 ? "0.0" : "0");

  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    let frameId;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;
      setDisplay(
        decimals > 0 ? current.toFixed(decimals) : String(Math.round(current))
      );
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [active, target, duration, decimals]);

  return display;
}

function MetricCard({ prefix, value, suffix, label, decimals, active }) {
  const display = useAnimatedCounter(value, 1500, active, decimals);
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricValue}>
        {prefix}
        {display}
        {suffix}
      </div>
      <div className={styles.metricLabel}>{label}</div>
    </div>
  );
}

const LeftPanel = () => {
  const { t } = useTranslation();
  const headlines = useMemo(
    () => [
      t("auth.headline_1"),
      t("auth.headline_2"),
      t("auth.headline_3"),
      t("auth.headline_4"),
      t("auth.headline_5"),
    ],
    [t],
  );
  const metrics = useMemo(
    () => [
      { prefix: "+", value: 34, suffix: "%", label: t("auth.metric_nps"), decimals: 0 },
      { prefix: "-", value: 28, suffix: "%", label: t("auth.metric_churn"), decimals: 0 },
      { prefix: "", value: 2.4, suffix: "x", label: t("auth.metric_insights"), decimals: 1 },
    ],
    [t],
  );
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [countersActive, setCountersActive] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setHeadlineIndex((i) => (i + 1) % headlines.length);
        setFadeIn(true);
      }, 500);
    }, 3000);
    return () => clearInterval(interval);
  }, [headlines.length]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCountersActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <aside className={styles.panelLeft} ref={panelRef}>
      <div className={styles.brandRow}>
        <img
          src={PIVONY_LOGO_ON_DARK_BG}
          alt="Pivony"
          className={styles.brandLogo}
        />
      </div>

      <div className={styles.hero}>
        <h1
          className={`${styles.headline} ${fadeIn ? styles.headlineVisible : styles.headlineHidden}`}
        >
          {headlines[headlineIndex]}
        </h1>
        <p className={styles.subtext}>{t("auth.subtext")}</p>

        <div className={styles.orbitContainer} aria-hidden="true">
          <div className={styles.orbitRing} />
          <div className={styles.orbitCenter}>
            <div className={styles.logoWrap}>
              <PivonyOrbitIcon size={64} className={styles.centerLogoSvg} />
            </div>
            <div className={styles.hostLabel}>{t("auth.insight_engine")}</div>
          </div>

          <div
            className={`${styles.integrationCard} ${styles.cardSocial}`}
            title={t("auth.source_social")}
          >
            <svg viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            </svg>
          </div>
          <div
            className={`${styles.integrationCard} ${styles.cardAppstore}`}
            title={t("auth.source_app_store")}
          >
            <svg viewBox="0 0 24 24">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
          </div>
          <div
            className={`${styles.integrationCard} ${styles.cardCall}`}
            title={t("auth.source_call_center")}
          >
            <svg viewBox="0 0 24 24">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zm7 9c0 3.53-2.4 6.44-5.65 7.32V20h-2.7v-2.68C7.4 16.44 5 13.53 5 10h2c0 2.76 2.24 5 5 5s5-2.24 5-5h2z" />
            </svg>
          </div>
          <div
            className={`${styles.integrationCard} ${styles.cardSurvey}`}
            title={t("auth.source_survey")}
          >
            <svg viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H5v-2h7v2zm7-4H5v-2h14v2zm0-4H5V7h14v2z" />
            </svg>
          </div>
          <div
            className={`${styles.integrationCard} ${styles.cardSupport}`}
            title={t("auth.source_support")}
          >
            <svg viewBox="0 0 24 24">
              <path d="M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4c1.1 0 2 .9 2 2s-.9 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-2-1.46c-1.19.69-2 1.99-2 3.46s.81 2.77 2 3.46V18H4v-2.54c1.19-.69 2-1.99 2-3.46 0-1.48-.8-2.77-2-3.46V6h16v2.54z" />
            </svg>
          </div>
        </div>

        <div className={styles.metricsRow}>
          {metrics.map((m) => (
            <MetricCard key={m.label} {...m} active={countersActive} />
          ))}
        </div>
      </div>
    </aside>
  );
};

export default LeftPanel;
